import { mat4, vec2, vec3 } from "gl-matrix";
import { Observable, Subject } from "rxjs";
import { takeWhile } from "rxjs/operators";
import TWEEN from "@tweenjs/tween.js";
import i18next, { i18n } from "i18next";

import { Bounds, TileLayer, TileMap, TileSet, GamePage, Positionable } from "./interfaces";
import { state } from "./index";
import { resetContext } from "./helpers/webgl-helpers";
import { gamePageBounds } from "./helpers/math-helpers";
import { makeInteractionCommand } from "./helpers/input-helpers";
import { moveCharacterAlongPath } from "./helpers/map-helpers";
import SpriteRenderer from "./sprite-renderer";
import GameSaver from "./game-saver";
import SettingsCog from "./settings-cog";

const { resize$ } = state.o$;

import {
  clearScreen,
  compiledProgram,
  cacheAttributeLocations,
  cacheUniformLocations,
  createMapTbn,
  enableBlending,
  setDisableDepthTesting,
  setHalfPixelTextureCoords,
  setQuadPositions,
  setNormalsAttribDirect,
  setPositionsAttribDirect,
  setBitangentsAttribDirect,
  setTangentsAttribDirect,
  setTextureCoordsAttribDirect,
  setOpacitiesAttribDirect,
  setDrawToScreen,
  setDrawToTexture
} from "./helpers/webgl-helpers";

import {
  TILE_TEXTURE_COORDS,
  QUAD_WIDTH,
  SCREEN_POSITIONS,
  SCREEN_TEXTURE_COORDS,
  QUAD_POSITIONS
} from "./models";
import KeyboardInput from "./inputs/keyboard-input";
import PointerInput from "./inputs/pointer-input";
import SPRITE_FRAG_SHADER from "./shaders/sprite-fragment.glsl";
import TILE_VERT_SHADER from "./shaders/tile-vertex.glsl";
import TEXTURE_FRAG_SHADER from "./shaders/texture-fragment.glsl";
import TEXTURE_VERT_SHADER from "./shaders/texture-vertex.glsl";
import TownPlay from "./town-play";
import Sprite from "~sprite";

const screenTextureCoords = new Float32Array(SCREEN_TEXTURE_COORDS);

const UNIFORM_NAMES = [
  "uWorldStart",
  "uWorldEnd",
  "uModelMatrix",
  "uViewMatrix",
  "uProjectionMatrix",
  "uSpriteSampler",
  "uNormalSampler",
  "uLightPos",
  "uLightPositions",
  "uLightColors",
  "uLightDropoffs",
  "uDayLevel",
  "uOpacity",
  "uNormals",
  "uTime"
];

const ATTRIBUTE_NAMES = [
  "aOpacity",
  "aVertexPosition",
  "aVertexNormal",
  "aTextureCoord",
  "aVertexTangent",
  "aVertexBitangent"
];

const vecToMessWith: vec3 = [0, 0, 0];

export default class Town implements GamePage {
  public menu: HTMLElement;
  public loaded: boolean;
  public tornDown: boolean;
  public routerId: string;
  public playerTarget: Positionable;
  public townPlay: TownPlay | null;
  public name: string = "generic-town";

  protected gameCanvas: HTMLCanvasElement;
  protected gameGl: WebGLRenderingContext;
  protected params: URLSearchParams;
  protected texture: WebGLTexture;
  protected normalTexture: WebGLTexture;
  protected previousLayerTexture: WebGLTexture;
  protected layerTextureCoords: Float32Array[];
  protected layerOpacities: number[];
  protected vertexPositions: Float32Array;
  protected layerProgramVertexPositions: Float32Array;
  protected vertexTangents: Float32Array;
  protected vertexBitangents: Float32Array;
  protected vertexNormals: Float32Array;
  protected vertexOpacities: Float32Array;
  protected tileMap: TileMap;
  protected tileSet: TileSet;
  protected tileLayer: TileLayer;
  protected vertexCount = 0;
  protected fb: WebGLFramebuffer;
  protected spriteRenderer: SpriteRenderer;
  protected townTime$: Subject<number>;
  protected overlay: HTMLElement;
  protected lightPositions: number[];
  protected lightColors: number[];
  protected lightDropoffs: number[];
  protected lockCameraToMapBounds = true;
  protected gameSaver: GameSaver;
  protected dayLevel: number = 1.0;
  protected i18next: i18n;

  private inputs;
  private bounds: Bounds;
  private tileAngle: vec3;
  private settingsCog: SettingsCog;

  constructor() {
    this.gameCanvas = <HTMLCanvasElement>document.getElementById("game");
    this.gameGl = this.gameCanvas.getContext("webgl", {
      antialias: false,
      depth: false,
      alpha: false
    });
    this.params = new URLSearchParams("");

    const { gameGl } = this;

    this.texture = gameGl.createTexture();
    this.normalTexture = gameGl.createTexture();
    this.layerTextureCoords = [];
    this.layerOpacities = [];
    this.vertexPositions = new Float32Array();
    this.layerProgramVertexPositions = new Float32Array(SCREEN_POSITIONS.slice());
    this.vertexTangents = new Float32Array();
    this.vertexBitangents = new Float32Array();
    this.vertexNormals = new Float32Array();
    this.vertexOpacities = new Float32Array();
    this.tileMap = null;
    this.tileSet = null;
    this.tileLayer = null;
    this.vertexCount = 0;
    this.fb = null;
    this.previousLayerTexture = gameGl.createTexture();
    this.inputs = [];
    this.loaded = false;
    this.tornDown = false;
    this.bounds = { top: -1, left: -1, right: -1, bottom: -1 };
    this.tileAngle = [0, 0, 0];
    this.spriteRenderer = new SpriteRenderer(gameGl, "main");
    this.townPlay = null;
    this.routerId = "";
    this.overlay = document.getElementById("overlay");
    this.lightPositions = new Array(12 * 3).fill(-1);
    this.lightColors = new Array(12 * 4).fill(-1);
    this.lightDropoffs = new Array(12).fill(1);
    this.gameSaver = new GameSaver();
    this.menu = document.createElement("div");

    this.configureInput();
    setDisableDepthTesting(gameGl);
    this.resize = this.resize.bind(this);
    resize$.pipe(takeWhile(() => !this.tornDown)).subscribe(this.resize);

    this.townTime$ = new Subject();
    this.i18next = i18next;
  }

  tearDown() {
    const { gameGl } = this;

    this.inputs.forEach(input => {
      input.tearDown();
    });

    this.townPlay.stop();

    resetContext(gameGl);
    this.tornDown = true;
  }

  configurePrograms() {
    const { gameGl } = this;
    const townProgram = compiledProgram(gameGl, TILE_VERT_SHADER, SPRITE_FRAG_SHADER);
    const layerProgram = compiledProgram(gameGl, TEXTURE_VERT_SHADER, TEXTURE_FRAG_SHADER);

    state.programWrappers.main = {
      program: townProgram,
      cache: { program: townProgram, attributes: {}, enabled: {}, uniforms: {} },
      buffers: {
        positions: gameGl.createBuffer(),
        normals: gameGl.createBuffer(),
        textureCoords: gameGl.createBuffer(),
        tangents: gameGl.createBuffer(),
        bitangents: gameGl.createBuffer(),
        opacities: gameGl.createBuffer()
      }
    };

    state.programWrappers.layer = {
      program: layerProgram,
      cache: { program: layerProgram, attributes: {}, enabled: {}, uniforms: {} },
      buffers: {
        positions: gameGl.createBuffer(),
        textureCoords: gameGl.createBuffer()
      }
    };

    cacheAttributeLocations(gameGl, "main", townProgram, state, ATTRIBUTE_NAMES);
    cacheUniformLocations(gameGl, "main", townProgram, state, UNIFORM_NAMES);
    cacheAttributeLocations(gameGl, "layer", layerProgram, state, [
      "aVertexPosition",
      "aTextureCoord"
    ]);
    cacheUniformLocations(gameGl, "layer", layerProgram, state, ["uLayerSampler"]);
  }

  configureInput() {
    const pointerInput = new PointerInput(state);
    const keyboarInput = new KeyboardInput(state);
    this.inputs.push(pointerInput);
    this.inputs.push(keyboarInput);
  }

  tick(time: number) {
    if (!this.loaded) return;

    this.townTime$.next(time);

    this.update(time);
    this.render(time);
  }

  update(time) {
    const { currentPlayer } = state;

    if (state.currentPlayer) {
      currentPlayer.update(time);
      const interact = makeInteractionCommand(this.playerTarget, this.townPlay);
      moveCharacterAlongPath(state.currentPlayer, state.playerPath, interact);
    }

    this.townPlay.characters.forEach(c => {
      if (c.sprite) c.sprite.update(time);
    });

    this.updateCamera();
    this.updateTilePositions();
  }

  // TODO: turn this into load function
  updateTilePositions() {
    if (this.vertexCount > 0 && this.vertexCount === this.vertexPositions.length) return;

    this.vertexPositions = new Float32Array(state.numTiles * QUAD_POSITIONS.length);
    this.vertexTangents = new Float32Array(state.numTiles * SpriteRenderer.TANGENT_LENGTH);
    this.vertexBitangents = new Float32Array(state.numTiles * SpriteRenderer.BITANGENT_LENGTH);
    this.vertexNormals = new Float32Array(state.numTiles * SpriteRenderer.NORMAL_LENGTH);
    this.vertexOpacities = new Float32Array(state.numTiles * QUAD_POSITIONS.length);

    this.vertexCount = this.vertexPositions.length;

    const width: number = this.tileMap.width;

    for (let i = 0; i < state.numTiles; i++) {
      const col: number = i % width; // column tile is in in the tileset
      const row: number = Math.floor(i / width); // row tile is in in the tileset

      const angle: number = this.tileAngle[1];

      setQuadPositions(this.vertexPositions, col, row, i, angle, vecToMessWith);
    }

    this.updateBitangents();
  }

  updateBitangents() {
    const {
      vertexPositions,
      vertexBitangents,
      vertexTangents,
      vertexNormals,
      vertexOpacities
    } = this;

    // Every vertexPosition is made up of the following...
    // Quads, made up of two triangles
    // Tiangles, made up of vertices
    // Vertices, made up of x, y, z coords

    // Go triangle by triangle
    let offset = 0;
    for (let i = 0; i < vertexPositions.length; i = i + 9) {
      const vertexPosition = vertexPositions.subarray(i, i + 9);
      const tbn = createMapTbn(vertexPosition);

      // Every vertex in a triangle can have the same tangent/bitangent
      // TODO Why does it need to be 6 and not 3?
      for (let j = 0; j < 6; j++) {
        this.vertexTangents.set(tbn.tangent, offset);
        this.vertexBitangents.set(tbn.bitangent, offset);
        this.vertexNormals.set(tbn.normal, offset);
        offset += 3;
      }
    }

    this.vertexOpacities.fill(1);
  }

  updateCamera() {
    const { camera, inverseViewProjectionMatrix } = state.viz;

    this.resetMatrices();

    this.bounds = gamePageBounds(inverseViewProjectionMatrix, camera.vector);

    if (!this.lockCameraToMapBounds) return;

    // stop camera at edge of map
    const leftEdgeIsShowing = this.bounds.left < 0;
    const rightEdgeIsShowing = this.bounds.right > this.tileMap.width;

    const topEdgeIsShowing = this.bounds.top > 0;
    const bottomEdgeIsShowing = this.bounds.bottom < -this.tileMap.height;
    const cameraTooWide = this.bounds.right - this.bounds.left > this.tileMap.width * QUAD_WIDTH;
    const cameraTooTall = this.bounds.top - this.bounds.bottom > this.tileMap.height * QUAD_WIDTH;
    const atAnEdge =
      leftEdgeIsShowing ||
      rightEdgeIsShowing ||
      topEdgeIsShowing ||
      bottomEdgeIsShowing ||
      cameraTooTall ||
      cameraTooWide;

    if (leftEdgeIsShowing) camera.moveX(-this.bounds.left);
    if (rightEdgeIsShowing) camera.moveX(-(this.bounds.right - this.tileMap.width * QUAD_WIDTH));
    if (topEdgeIsShowing) camera.moveY(-this.bounds.top);
    if (bottomEdgeIsShowing) camera.moveY(-(this.tileMap.height * QUAD_WIDTH + this.bounds.bottom));
    if (cameraTooWide) camera.updateX((this.tileMap.width * QUAD_WIDTH) / 2);
    if (cameraTooTall) camera.updateY((-this.tileMap.height * QUAD_WIDTH) / 2);

    if (atAnEdge) {
      this.resetMatrices();
      this.bounds = gamePageBounds(inverseViewProjectionMatrix, camera.vector);
    }
  }

  render(timestamp: number) {
    this.configureTownProgram(timestamp);
    this.drawTilesToTexture(timestamp);
    this.drawTilesToScreen();
    this.configureTownProgram(timestamp);
    this.drawCharactersToScreen(timestamp);
    this.gameGl.flush();
  }

  drawCharactersToScreen(timestamp) {
    const { gameGl } = this;
    const { programWrappers } = state;
    const { main } = programWrappers;
    const { cache } = main;
    setDrawToScreen(gameGl);
    gameGl.uniform1f(cache.uniforms.uOpacity, 1.0);
    // this.spriteRenderer.render(timestamp);
    this.spriteRenderer.renderNoGarbage(timestamp);
  }

  resize() {
    const { gameGl } = this;

    const width = state.canvasDimensions[0];
    const height = state.canvasDimensions[1];

    this.fb = gameGl.createFramebuffer();
    setDrawToTexture(gameGl, this.fb);
    const attachmentPoint = gameGl.COLOR_ATTACHMENT0;
    this.previousLayerTexture = gameGl.createTexture();
    gameGl.bindTexture(gameGl.TEXTURE_2D, this.previousLayerTexture);
    // define size and format of level 0
    const level = 0;
    const internalFormat = gameGl.RGBA;
    const border = 0;
    const format = gameGl.RGBA;
    const type = gameGl.UNSIGNED_BYTE;
    const data = null;
    gameGl.texImage2D(
      gameGl.TEXTURE_2D,
      level,
      internalFormat,
      width,
      height,
      border,
      format,
      type,
      data
    );

    // set the filtering so we don't need mips
    gameGl.texParameteri(gameGl.TEXTURE_2D, gameGl.TEXTURE_MIN_FILTER, gameGl.LINEAR);
    gameGl.texParameteri(gameGl.TEXTURE_2D, gameGl.TEXTURE_WRAP_S, gameGl.CLAMP_TO_EDGE);
    gameGl.texParameteri(gameGl.TEXTURE_2D, gameGl.TEXTURE_WRAP_T, gameGl.CLAMP_TO_EDGE);

    gameGl.framebufferTexture2D(
      gameGl.FRAMEBUFFER,
      attachmentPoint,
      gameGl.TEXTURE_2D,
      this.previousLayerTexture,
      level
    );

    setDrawToScreen(gameGl);
  }

  resetMatrices() {
    const { viz } = state;
    const {
      projectionMatrix,
      fieldOfView,
      zNear,
      zFar,
      viewProjectionMatrix,
      viewMatrix,
      inverseViewProjectionMatrix
    } = viz;
    this.resetViewMatrix();
    mat4.perspective(projectionMatrix, fieldOfView, state.aspect, zNear, zFar);
    mat4.multiply(viewProjectionMatrix, projectionMatrix, viewMatrix);
    mat4.invert(inverseViewProjectionMatrix, viewProjectionMatrix);
  }

  resetViewMatrix() {
    const { viz } = state;
    const { viewMatrix, camera, up } = viz;
    const { vector: camVector } = camera;
    mat4.identity(viewMatrix);
    mat4.lookAt(viewMatrix, camVector, [camVector[0], camVector[1], 0], up);
  }

  drawTilesToTexture(timestamp: number) {
    const {
      gameGl,
      fb,
      layerTextureCoords,
      texture,
      normalTexture,
      vertexNormals,
      vertexPositions,
      vertexTangents,
      vertexBitangents,
      vertexOpacities
    } = this;
    const { programWrappers } = state;
    const { main } = programWrappers;
    const { cache, buffers } = main;
    const { viz } = state;
    const modelMatrix = viz.modelMatrix as number[];

    // Clear composite tiles
    setDrawToTexture(gameGl, fb);
    clearScreen(gameGl);

    // Tile map
    gameGl.activeTexture(gameGl.TEXTURE0);
    gameGl.bindTexture(gameGl.TEXTURE_2D, texture);

    // Normal map
    gameGl.activeTexture(gameGl.TEXTURE1);
    gameGl.bindTexture(gameGl.TEXTURE_2D, normalTexture);

    gameGl.uniform1i(cache.uniforms.uSpriteSampler, 0);
    gameGl.uniform1i(cache.uniforms.uNormalSampler, 1);
    gameGl.uniformMatrix4fv(cache.uniforms.uModelMatrix, false, modelMatrix);

    setNormalsAttribDirect(gameGl, "main", state, buffers.normals, vertexNormals);
    setPositionsAttribDirect(gameGl, "main", state, buffers.positions, vertexPositions);
    setNormalsAttribDirect(gameGl, "main", state, buffers.normals, vertexNormals);
    setTangentsAttribDirect(gameGl, "main", state, buffers.tangents, vertexTangents);
    setBitangentsAttribDirect(gameGl, "main", state, buffers.bitangents, vertexBitangents);
    setOpacitiesAttribDirect(gameGl, "main", state, buffers.opacities, vertexOpacities);

    // Draw tiles to texture
    layerTextureCoords.forEach((textureCoords, i) => {
      this.drawTileTextures(textureCoords, i);
    });

    gameGl.uniform1f(cache.uniforms.uOpacity, 1.0);
  }

  configureTownProgram(timestamp: number) {
    const { gameGl, vertexTangents, vertexBitangents, tileMap } = this;
    const { programWrappers } = state;
    const { main } = programWrappers;
    const { program, cache, buffers } = main;
    const { viz } = state;
    const { projectionMatrix, modelMatrix, viewMatrix } = viz;
    gameGl.useProgram(program);
    enableBlending(gameGl);
    gameGl.uniformMatrix4fv(cache.uniforms.uProjectionMatrix, false, projectionMatrix);
    gameGl.uniformMatrix4fv(cache.uniforms.uModelMatrix, false, modelMatrix);
    gameGl.uniformMatrix4fv(cache.uniforms.uViewMatrix, false, viewMatrix);
    gameGl.uniform1f(cache.uniforms.uOpacity, 1.0);
    gameGl.uniform1f(cache.uniforms.uDayLevel, this.dayLevel);
    gameGl.uniform1f(cache.uniforms.uTime, timestamp / 200);
    gameGl.uniform2fv(cache.uniforms.uWorldStart, [0, 0]);
    gameGl.uniform2fv(cache.uniforms.uWorldEnd, [tileMap.width, tileMap.height]);
    gameGl.uniform3fv(cache.uniforms.uLightPos, [1.5 + 2 * Math.sin(timestamp / 300), -3.1, 1.0]);
    gameGl.uniform3fv(cache.uniforms.uLightPositions, this.lightPositions);
    gameGl.uniform4fv(cache.uniforms.uLightColors, this.lightColors);
    gameGl.uniform1fv(cache.uniforms.uLightDropoffs, this.lightDropoffs);
    setTangentsAttribDirect(gameGl, "main", state, buffers["tangents"], vertexTangents);
    setBitangentsAttribDirect(gameGl, "main", state, buffers["bitangents"], vertexBitangents);
  }

  drawTileTextures(textureCoords: Float32Array, index) {
    const { gameGl, layerOpacities, vertexPositions } = this;
    const { programWrappers } = state;
    const { main } = programWrappers;
    const { buffers, cache } = main;

    setTextureCoordsAttribDirect(gameGl, "main", state, buffers["textureCoords"], textureCoords);
    gameGl.uniform1f(cache.uniforms.uOpacity, layerOpacities[index]);
    gameGl.drawArrays(gameGl.TRIANGLES, 0, vertexPositions.length / 3);
  }

  drawTilesToScreen() {
    const { gameGl, layerProgramVertexPositions, previousLayerTexture } = this;
    const { programWrappers } = state;
    const { layer: layerWrapper } = programWrappers;
    const { program: layerProgram } = layerWrapper;
    const { buffers: layerBuffers } = layerWrapper;
    const { cache: layerCache } = layerWrapper;

    setDrawToScreen(gameGl);
    clearScreen(gameGl);

    gameGl.useProgram(layerProgram);

    setPositionsAttribDirect(
      gameGl,
      "layer",
      state,
      layerBuffers.positions,
      layerProgramVertexPositions
    );

    setTextureCoordsAttribDirect(
      gameGl,
      "layer",
      state,
      layerBuffers.textureCoords,
      screenTextureCoords
    );

    // Previous layer tiles texture
    gameGl.activeTexture(gameGl.TEXTURE0);
    gameGl.bindTexture(gameGl.TEXTURE_2D, previousLayerTexture);
    gameGl.uniform1i(layerCache.uniforms.uLayerSampler, 0);

    // Draw composite tiles
    gameGl.drawArrays(gameGl.TRIANGLES, 0, layerProgramVertexPositions.length / 3);
  }

  configureTileInAnimation() {
    const { tileMap, layerTextureCoords, layerOpacities, tileSet } = this;

    const tileSize: vec2 = [tileMap.tilewidth, tileMap.tileheight];
    const tileSetDimensions: vec2 = [tileSet.imagewidth, tileSet.imageheight];

    const angle: vec3 = [0, (3 * Math.PI) / 2 / 2, 0];

    const angleTween$: Observable<vec3> = new Observable(subscriber => {
      new TWEEN.Tween(angle)
        .to([0, 0, 0], 1000)
        .onUpdate(d => subscriber.next(d))
        .onComplete(d => {
          subscriber.next(d);
          subscriber.complete();
        })
        .start();
    });

    angleTween$.subscribe(angle => vec3.set(this.tileAngle, angle[0], angle[1], angle[2]));

    for (let i = 0; i < tileMap.layers.length; i++) {
      const layer = tileMap.layers[i];
      const tiles = layer.data;
      const textureCoords = new Float32Array(TILE_TEXTURE_COORDS.length * tiles.length);
      layerTextureCoords.push(textureCoords);
      layerOpacities.push(layer.opacity);
      for (let j = 0; j < tiles.length; j++) {
        const tileIndex = tiles[j] - 1;
        setHalfPixelTextureCoords(
          textureCoords,
          TILE_TEXTURE_COORDS,
          j,
          tileIndex,
          tileSize,
          tileSetDimensions
        );
      }
    }
  }

  configureStateWithCurrentMap() {
    // TODO clean up gamestate so it doesn't need tileSet
    // e.g. use tileLayer and tileMap directly
    state.numTiles = this.tileLayer.data.length;
    state.tileLayer = this.tileLayer;
    state.tileSet = this.tileSet;
    state.tileMap = this.tileMap;
  }

  async load(params) {
    const name = this.name;
    const dialogue = await import(`./routes/${name}.en.json`).then(i => i.default);
    const en = {};
    en[name] = dialogue;

    this.settingsCog = new SettingsCog(`/${name}`, "bottom");
    this.settingsCog.append(this.menu);

    await this.i18next.init({
      ns: [name],
      lng: "en",
      resources: { en }
    });
  }

  start() {
    state.currentTown = this;
    this.townPlay.start();
  }

  protected t(character: string, key: string) {
    return this.i18next.t(`${this.name}:${character}.${key}`);
  }
}
